import compact from "lodash/compact";
import partition from "lodash/partition";
import { ExperienceMultiToggle } from "PFApp/components/experience_multi_toggle";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { SidePanelType, useSidePanel, useSidePanelCloseExternal } from "PFApp/stores/side_panels_store";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { LoadingDots } from "PFComponents/loading_dots";
import SidePanel from "PFComponents/side_panel/side_panel";
import { Typography } from "PFComponents/typography";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { useBulkDictionaryConnectionCreate } from "PFCore/hooks/queries/custom_values/use_bulk_dictionary_connections_create";
import { Experience, Profile, ProfileCustomValue, SkillsFrameworkSkill } from "PFTypes";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { BooleanBulkSetting } from "./boolean_bulk_setting";
import { InformationBox } from "./information_box";
import css from "./skills_bulk_edit_side_panel.module.scss";
import { canTagSkillsAs } from "./skills_bulk_edit_side_panel.utils";

export type SkillsBulkEditSidePanelProps = {
  profile: Profile;
  selectedSkills: (ProfileCustomValue | SkillsFrameworkSkill)[];
  onSuccess: VoidFunction;
  onClose: VoidFunction;
};

type BooleanBulkSettingState = {
  enabled: boolean;
  value: boolean;
};

export const SkillsBulkEditSidePanel = ({
  profile,
  selectedSkills,
  onSuccess,
  onClose
}: SkillsBulkEditSidePanelProps) => {
  const { t: tTranslation } = useTranslation();
  const { t } = useTranslation("profiles", { keyPrefix: "show.parts.skillsSection.bulk" });
  const { data: currentAccount } = useCurrentAccount();
  const { showGrowl } = useGrowlStoreActions();
  const { isClosing, index } = useSidePanel(SidePanelType.ProfileBulkEditSkills);
  const closeWithAnimation = useSidePanelCloseExternal();
  const {
    config: {
      profile: { developmental_skills_limit: developmentalSkillsLimit, top_skills_limit: coreSkillsLimit }
    }
  } = currentAccount;

  const { mutateAsync: bulkCreateDictionaryConnection, isPending } = useBulkDictionaryConnectionCreate();

  const [experience, setExperience] = useState<Experience | null>(null);
  const [coreSetting, setCoreSetting] = useState<BooleanBulkSettingState>({ enabled: false, value: false });
  const [developmentalSetting, setDevelopmentalSetting] = useState<BooleanBulkSettingState>({
    enabled: false,
    value: false
  });

  useEffect(() => {
    if (selectedSkills.length === 0) {
      closeWithAnimation(SidePanelType.ProfileBulkEditSkills);
    }
  }, [selectedSkills.length]);

  const [existingSkills, missingSkills] = useMemo(
    () => partition(selectedSkills, (skill) => !!(skill as ProfileCustomValue).experience),
    [selectedSkills]
  );

  const handleSave = (onSidePanelClose: VoidFunction) => {
    const payload = selectedSkills.map((selectedSkill) => ({
      customValueId: (selectedSkill as SkillsFrameworkSkill).skillId ?? selectedSkill.id,
      experience: experience!,
      ...(coreSetting.enabled ? { top: coreSetting.value } : {}),
      ...(developmentalSetting.enabled ? { developmental: developmentalSetting.value } : {})
    }));
    bulkCreateDictionaryConnection(payload).then((results) => {
      const isError = results.some((result) => result.status === "rejected");
      if (isError) {
        const failedSkillsNames = compact(
          results.map(({ status }, index) => (status === "rejected" ? selectedSkills[index]?.value : null))
        );

        showGrowl({
          message: tTranslation("customValueSidepanel.saveError", { value: failedSkillsNames.join(", ") }),
          kind: "error"
        });
      } else {
        showGrowl({ message: t("bulkSaveSuccess") });
        onSuccess();
      }
      onSidePanelClose();
    });
  };

  const handleRemove = (onSidePanelClose: VoidFunction) => {
    const payload = existingSkills.map(({ id }) => ({
      customValueId: id,
      experience: Experience.None
    }));
    bulkCreateDictionaryConnection(payload).then((results) => {
      const isError = results.some((result) => result.status === "rejected");
      if (isError) {
        const failedSkillsNames = compact(
          results.map(({ status }, index) => (status === "rejected" ? existingSkills[index]?.value : null))
        );

        showGrowl({
          message: tTranslation("customValueSidepanel.removeError", { value: failedSkillsNames.join(", ") }),
          kind: "error"
        });
      } else {
        showGrowl({ message: t("bulkRemoveSuccess") });
        onSuccess();
      }
      onSidePanelClose();
    });
  };

  const canTagAsCore = canTagSkillsAs("top", profile.skills || [], selectedSkills, coreSkillsLimit);
  const canTagAsDevelopmental = canTagSkillsAs(
    "developmental",
    profile.skills || [],
    selectedSkills,
    developmentalSkillsLimit
  );
  const isCoreError = coreSetting.enabled && coreSetting.value && !canTagAsCore;
  const isDevelopmentalError =
    developmentalSetting.enabled && developmentalSetting.value && !canTagAsDevelopmental;

  const renderFooter = ({ onSidePanelClose }: { onSidePanelClose: VoidFunction }) => (
    <Inline gap="spacingLg" justifyContent="space-between">
      <Inline gap="spacingLg">
        <Button kind="secondary" text={tTranslation("cancel")} onClick={onSidePanelClose} />
        <Button
          kind="danger"
          disabled={existingSkills.length === 0}
          text={`${tTranslation("remove")} (${existingSkills.length})`}
          onClick={() => handleRemove(onSidePanelClose)}
        />
      </Inline>
      <Button
        kind="primary"
        disabled={
          (!experience && !coreSetting.enabled && !developmentalSetting.enabled) ||
          (!experience && missingSkills.length > 0) ||
          isCoreError ||
          isDevelopmentalError ||
          isPending
        }
        text={`${tTranslation("save")} (${selectedSkills.length})`}
        onClick={() => handleSave(onSidePanelClose)}
      />
    </Inline>
  );

  return (
    <SidePanel
      show
      fullHeight
      zIndex={index}
      isSidePanelClosing={isClosing}
      title={t("editSkills", { count: selectedSkills.length })}
      footerRenderer={renderFooter}
      onClose={onClose}
    >
      {!!isPending && (
        <div className={css.savingOverlay}>
          <LoadingDots circlesEnabled circleSize="xl" />
        </div>
      )}
      <Stack gap="spacingLg">
        <InformationBox skills={selectedSkills} />
        <ExperienceMultiToggle experience={experience} onChange={setExperience} required={false} />
        <BooleanBulkSetting
          enabled={coreSetting.enabled}
          onEnabledChange={(enabled) => setCoreSetting({ enabled, value: false })}
          value={coreSetting.value}
          onValueChange={(value) => setCoreSetting((setting) => ({ ...setting, value }))}
          title={t("coreTag")}
          tooltip={t("coreTagTooltip")}
          valueLabel={
            <Inline gap="spacingSm" alignItems="center">
              <Icon name="core" />
              <Typography variant="bodyRegular">{t("core")}</Typography>
            </Inline>
          }
          error={isCoreError ? tTranslation("coreSkillsLimit", { limit: coreSkillsLimit }) : undefined}
        />
        <BooleanBulkSetting
          enabled={developmentalSetting.enabled}
          onEnabledChange={(enabled) => setDevelopmentalSetting({ enabled, value: false })}
          value={developmentalSetting.value}
          onValueChange={(value) => setDevelopmentalSetting((setting) => ({ ...setting, value }))}
          title={t("developmentalTag")}
          tooltip={t("developmentalTagTooltip")}
          valueLabel={
            <Inline gap="spacingSm" alignItems="center">
              <Icon name="development" />
              <Typography variant="bodyRegular">{t("developmental")}</Typography>
            </Inline>
          }
          error={
            isDevelopmentalError
              ? tTranslation("developmentalSkillsLimit", { limit: developmentalSkillsLimit })
              : undefined
          }
        />
      </Stack>
    </SidePanel>
  );
};
